import type { Config } from "tailwindcss";
import defaultTheme from "tailwindcss/defaultTheme";
import plugin from "tailwindcss/plugin";
import tailwindcssAnimate from "tailwindcss-animate";
import typography from "@tailwindcss/typography";
import containerQueries from "@tailwindcss/container-queries";

export default {
  darkMode: ["class"],
  theme: {
    extend: {
      colors: {
        brand: {
          50: "#fef5e7",
          100: "#FCE6BF",
          200: "#FBD99D",
          300: "#F9C771",
          400: "#F7B440",
          500: "#F59E0B",
          600: "#DC8F09",
          700: "#BF7C08",
          800: "#A26907",
          900: "#764C05",
          950: "#4E3303",
          DEFAULT: "#F59E0B",
          foreground: "#000000",
        },
        // info: tenant-branding default for the "info" semantic role
        // (consumed by apps/admin/.../branding/utils.ts). Distinct from
        // `blue` (Sunfish substrate) so tenants get a brighter info-blue
        // rather than the deep navy.
        info: {
          50: "#EAF3FB",
          100: "#CDE3F6",
          200: "#A2CAEE",
          300: "#6FACDD",
          400: "#3F8DD0",
          500: "#1D7ED8",
          600: "#1968B0",
          700: "#155389",
          800: "#103E66",
          900: "#0A2C4C",
          DEFAULT: "#1D7ED8",
          foreground: "#FFFFFF",
        },
        // blue: the Sunfish identity navy. The 500 step is the canonical deep
        // navy used on the sidebar and default-variant button. Below 500 the
        // scale lightens through info/sky territory; above 500 it darkens
        // toward a near-black navy (never pure black, per DESIGN.md's
        // No-Pure-Color Rule).
        blue: {
          50: "#DBEBFA",
          100: "#C4DFF7",
          200: "#9CC8F2",
          300: "#61A8EA",
          400: "#1D7ED8",
          500: "#0A2C4C",
          600: "#091F38",
          700: "#071829",
          800: "#05121E",
          900: "#040C15",
          950: "#02080F",
          DEFAULT: "#0A2C4C",
          foreground: "#FFFFFF",
        },
        // cream: the warm accent-surface family from the brand handoff.
        // 50–200 surfaces, 300–400 borders, 500–700 decorative muted golds,
        // 800 the AA-contrast gold for text on cream surfaces.
        cream: {
          50: "#FFFDF8",
          100: "#FFF7E9",
          200: "#FFF3DC",
          300: "#F1E4C9",
          350: "#EEE7D8",
          400: "#E7DFCF",
          500: "#a99164",
          600: "#9a8f7d",
          700: "#8a8071",
          800: "#756545",
          DEFAULT: "#FFF7E9",
          foreground: "#1a1712",
        },
        green: {
          50: "#f0fdf4",
          100: "#dcfce7",
          200: "#bbf7d0",
          300: "#86efac",
          400: "#4ade80",
          500: "#22c55e",
          600: "#16a34a",
          700: "#15803d",
          800: "#166534",
          900: "#14532d",
          950: "#052e16",
          DEFAULT: "#16a34a",
          foreground: "#FFFFFF",
        },
        gray: {
          DEFAULT: "#4a4f55",
          50: "#f9fafb",
          100: "#f3f4f6",
          200: "#e5e7eb",
          300: "#d1d5dc",
          400: "#5a5f7d",
          500: "#4a4f5f",
          600: "#41434b",
          700: "#374151",
          800: "#1f2937",
          900: "#111827",
          950: "#0d0e11",
          foreground: "#FFFFFF",
        },
        background: "hsl(var(--background))",
        foreground: "hsl(var(--foreground))",
        transparent: "transparent",
        card: {
          DEFAULT: "hsl(var(--card))",
          foreground: "hsl(var(--card-foreground))",
        },
        "card-alternate": {
          DEFAULT: "hsl(var(--card-alternate))",
          foreground: "hsl(var(--card-alternate-foreground))",
        },
        popover: {
          DEFAULT: "hsl(var(--popover))",
          foreground: "hsl(var(--popover-foreground))",
        },
        primary: {
          DEFAULT: "hsl(var(--primary))",
          foreground: "hsl(var(--primary-foreground))",
        },
        secondary: {
          DEFAULT: "hsl(var(--secondary))",
          foreground: "hsl(var(--secondary-foreground))",
        },
        muted: {
          DEFAULT: "hsl(var(--muted))",
          foreground: "hsl(var(--muted-foreground))",
        },
        accent: {
          DEFAULT: "hsl(var(--accent))",
          foreground: "hsl(var(--accent-foreground))",
        },
        destructive: {
          DEFAULT: "hsl(var(--destructive))",
          foreground: "hsl(var(--destructive-foreground))",
        },
        positive: {
          DEFAULT: "hsl(var(--positive))",
          foreground: "hsl(var(--positive-foreground))",
        },
        warning: {
          DEFAULT: "hsl(var(--warning))",
          foreground: "hsl(var(--warning-foreground))",
        },
        border: "hsl(var(--border))",
        input: "hsl(var(--input))",
        ring: "hsl(var(--ring))",
        chart: {
          "1": "hsl(var(--chart-1))",
          "2": "hsl(var(--chart-2))",
          "3": "hsl(var(--chart-3))",
          "4": "hsl(var(--chart-4))",
          "5": "hsl(var(--chart-5))",
        },
        sidebar: {
          DEFAULT: "hsl(var(--sidebar-background))",
          foreground: "hsl(var(--sidebar-foreground))",
          primary: "hsl(var(--sidebar-primary))",
          "primary-foreground": "hsl(var(--sidebar-primary-foreground))",
          accent: "hsl(var(--sidebar-accent))",
          "accent-foreground": "hsl(var(--sidebar-accent-foreground))",
          border: "hsl(var(--sidebar-border))",
          ring: "hsl(var(--sidebar-ring))",
        },
      },
      fontFamily: {
        sans: ["'Hanken Grotesk Variable'", ...defaultTheme.fontFamily.sans],
        display: ["'Bricolage Grotesque Variable'", "'Hanken Grotesk Variable'", ...defaultTheme.fontFamily.sans],
      },
      borderRadius: {
        lg: "var(--radius)",
        md: "calc(var(--radius) - 2px)",
        sm: "calc(var(--radius) - 4px)",
      },
      // Brand typography scale (Bricolage/Hanken hierarchy). Use these aliases
      // instead of raw text-2xl / text-sm so the named hierarchy stays in code.
      // display/headline/title pair with font-display (Bricolage); body/label/
      // eyebrow stay font-sans (Hanken). Eyebrow callers add `uppercase`.
      // These aliases set size/weight/leading only, not family: display/
      // headline/title call sites must add `font-display` themselves.
      fontSize: {
        display: ["2.25rem", { lineHeight: "0.92", letterSpacing: "-0.02em", fontWeight: "800" }],
        headline: ["1.5rem", { lineHeight: "1.0", letterSpacing: "-0.01em", fontWeight: "800" }],
        title: ["1.25rem", { lineHeight: "1.1", fontWeight: "700" }],
        body: ["0.875rem", { lineHeight: "1.5", fontWeight: "400" }],
        label: ["0.75rem", { lineHeight: "1.4", fontWeight: "500" }],
        eyebrow: ["0.75rem", { lineHeight: "1.4", letterSpacing: "0.1em", fontWeight: "700" }],
      },
      // DESIGN.md spacing scale aliases. Tailwind's default p-1/p-2/p-4/p-6/p-8
      // already cover these values; the aliases just let code reference the
      // DESIGN.md token names directly (p-xs, gap-md, etc.).
      spacing: {
        xs: "0.25rem",
        sm: "0.5rem",
        md: "1rem",
        lg: "1.5rem",
        xl: "2rem",
      },
      maxWidth: {
        page: "1200px",
        "11/12": "91.666667%",
      },
      keyframes: {
        bob: {
          "0%, 100%": { transform: "translateY(0) rotate(-5deg)" },
          "50%": { transform: "translateY(-14px) rotate(5deg)" },
        },
        "fish-pulse": {
          "0%, 100%": { transform: "scale(1)" },
          "45%": { transform: "scale(1.16)" },
        },
        float: {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-9px)" },
        },
        swim: {
          from: { transform: "translateX(var(--swim-from, -40px))" },
          to: { transform: "translateX(var(--swim-to, 230px))" },
        },
        "fish-fade": {
          "0%, 100%": { opacity: "0.25", transform: "translateY(0)" },
          "50%": { opacity: "1", transform: "translateY(-6px)" },
        },
        shimmer: {
          to: { backgroundPosition: "200% 0" },
        },
        ckpop: {
          "0%": { transform: "scale(1)" },
          "45%": { transform: "scale(1.65) rotate(8deg)" },
          "100%": { transform: "scale(1)" },
        },
        bub: {
          "0%": { transform: "translateY(0) scale(.5)", opacity: "0" },
          "25%": { opacity: ".9" },
          "100%": { transform: "translateY(-48px) scale(1.1)", opacity: "0" },
        },
        "bar-in": {
          "0%": { opacity: "0", transform: "translateY(-8px)" },
          "100%": { opacity: "1", transform: "translateY(0)" },
        },
        "goal-pop": {
          "0%": { transform: "scale(0)" },
          "70%": { transform: "scale(1.25)" },
          "100%": { transform: "scale(1)" },
        },
        "fish-surface": {
          "0%": { transform: "scale(.25) rotate(-14deg)", opacity: ".25" },
          "60%": { opacity: "1" },
          "80%": { transform: "scale(1.06) rotate(2deg)" },
          "100%": { transform: "scale(1) rotate(0)" },
        },
        "fish-ripple": {
          "0%": { transform: "scale(.35)", opacity: ".85" },
          "100%": { transform: "scale(2)", opacity: "0" },
        },
        "fish-drop": {
          "0%": { transform: "translate(0,0) scale(1)", opacity: "0" },
          "15%": { opacity: "1" },
          "100%": { transform: "translate(var(--dx),var(--dy)) scale(.3)", opacity: "0" },
        },
        "wave-drift": {
          "0%": { transform: "translateX(0)" },
          "100%": { transform: "translateX(-50%)" },
        },
      },
      animation: {
        bob: "bob 3.6s ease-in-out infinite",
        "bob-slow": "bob 4.2s ease-in-out infinite",
        "fish-spin": "spin 1.6s cubic-bezier(.5,0,.5,1) infinite",
        "fish-spin-fast": "spin 1.2s linear infinite",
        orbit: "spin 1.4s linear infinite",
        "fish-pulse": "fish-pulse 2s ease-in-out infinite",
        float: "float 2.8s ease-in-out infinite",
        swim: "swim 2.4s ease-in-out infinite",
        "swim-slow": "swim 3s ease-in-out 0.5s infinite",
        "fish-fade": "fish-fade 1.1s ease-in-out infinite",
        shimmer: "shimmer 1.4s linear infinite",
        ckpop: "ckpop .4s cubic-bezier(.34,1.56,.64,1) both",
        bub: "bub 1s ease-out both",
        "bar-in": "bar-in .3s cubic-bezier(.3,.8,.4,1) both",
        "goal-pop": "goal-pop .45s cubic-bezier(.34,1.56,.64,1) both",
        "fish-surface": "fish-surface .9s cubic-bezier(.3,.8,.4,1) both",
        "fish-ripple": "fish-ripple .8s ease-out both",
        "fish-drop": "fish-drop .9s ease-out .55s both",
        "wave-drift": "wave-drift 20s linear infinite",
      },
    },
  },
  plugins: [
    tailwindcssAnimate,
    typography,
    containerQueries,
    plugin(({ addVariant }) => {
      addVariant("not-last", "&:not(:last-child)");
    }),
  ],
  content: [],
} satisfies Config;
